// @flow

import { Animation } from './animation';
import { BaseRenderer } from './base.renderer';
import { ChartModel } from './chart.model';
import { scale } from './utils/array.util';
import { hexToRgbA } from './utils/color.utils';

export class BaseChartRenderer extends BaseRenderer {
    chartMax: number;
    lineWidth: number;
    drawFrom: number;
    drawTo: number;
    yScaledMax1: number;
    yScaledMax2: number;

    constructor (chart: ChartModel, width: number, height: number, lineWidth: number) {
        super(chart, width, height);
        this.lineWidth = lineWidth;
        this.drawFrom = 0;
        this.drawTo = this.chart.row.data.length;
    }

    render () {
        this.clear();
        const x0 = this.drawFrom;
        const xl = this.drawTo;
        const { width, height } = this.canvas;
        const step = width / ((this.drawTo - 1) - this.drawFrom);
        this.context.lineWidth = this.lineWidth;
        if (!this.chart.stacked || this.chart.stack.length === 0) {
            for (let j = 0; j < this.chart.columns.length; j++) {
                const column = this.chart.columns[j];
                if (column.type === 'line' || column.type === 'area') {
                    this.context.strokeStyle = hexToRgbA(column.color, column._opacity);
                    if (column._opacity > 0) {
                        this.context.beginPath();
                        if (column.type === 'area') {
                            this.context.fillStyle = this.context.strokeStyle;
                            this.context.moveTo(0, height);
                            this.context.lineTo(0, scale(column.data[this.drawFrom], height, this.chartMax));
                        } else {
                            this.context.moveTo(0, scale(column.data[this.drawFrom], height, this.chart.yScaled ? (j === 0 ? this.yScaledMax1 : this.yScaledMax2) : this.chartMax));
                        }
                        for (let i = x0; i < xl; i++) {
                            const x = ((i - this.drawFrom) * step);
                            this.context.lineTo(x, scale(column.data[i], height, this.chart.yScaled ? (j === 0 ? this.yScaledMax1 : this.yScaledMax2) : this.chartMax));
                        }
                        if (column.type === 'area') {
                            this.context.lineTo(width, height);
                            this.context.fill();
                        } else {
                            this.context.stroke();
                        }
                    }
                } else if (column.type === 'bar') {
                    this.context.fillStyle = column.color;
                    if (column._opacity > 0) {
                        let pervX = 0;
                        for (let i = x0; i < xl; i++) {
                            const x = ((i - this.drawFrom) * step);
                            const y = scale(column.data[i], height, this.chartMax);
                            this.context.fillRect(pervX, y, x - pervX, height - y);
                            pervX = x;
                        }
                    }
                }
            }
        } else {
            let pervX = 0;
            for (let i = x0; i < xl; i++) {
                const cols = this.chart.stack[i];
                const x = ((i - this.drawFrom) * step);
                for (let j = 0; j < cols.length; j++) {
                    const col = cols[j];
                    if (col.column._opacity > 0) {
                        this.context.fillStyle = col.column.color; //hexToRgbA(col.column.color, col.column._opacity);
                        const y = scale(col.val, height, this.chartMax);
                        this.context.fillRect(pervX, y, x - pervX, height - y);
                    }
                }
                pervX = x;
            }
        }
    }

    draw () {
        if (this.progressAnimation) {
            if (this.animations.toggleColumn) {
                const { animation, column } = this.animations.toggleColumn;
                column._opacity = animation.getValue();
                if (animation.finished()) {
                    delete this.animations.toggleColumn;
                }
            }
            if (this.animations.chartMax) {
                const { animation, animation1, animation2 } = this.animations.chartMax;
                if (this.chart.yScaled) {
                    this.yScaledMax1 = animation1.getValue();
                    this.yScaledMax2 = animation2.getValue();
                    if (animation1.finished() && animation2.finished()) {
                        delete this.animations.chartMax;
                    }
                } else {
                    this.chartMax = animation.getValue();
                    if (animation.finished()) {
                        delete this.animations.chartMax;
                    }
                }
            }
            this.progressAnimation = !!(this.animations.chartMax || this.animations.toggleColumn);
            this.render();
        }
        return this.canvas;
    }

    update (changes: any) {
        this.progressAnimation = true;
        if (changes.chartMax !== undefined) {
            if (this.chart.yScaled) {
                this.animations.chartMax = {
                    animation1: new Animation(this.yScaledMax1, this.chart.columns[0].getMaxColumnValue(this.chart.selectStart, this.chart.selectEnd), 1),
                    animation2: new Animation(this.yScaledMax2, this.chart.columns[1].getMaxColumnValue(this.chart.selectStart, this.chart.selectEnd), 1)
                };
            } else {
                this.animations.chartMax = { animation: new Animation(this.chartMax, changes.chartMax, 1) };
            }
        }
        if (changes.toggleColumn !== undefined) {
            const column = this.chart.columns.find(c => c.key === changes.toggleColumn);
            let animation = null;
            if (column && column._disabled) {
                animation = new Animation(1, 0, 0.1);
            } else {
                animation = new Animation(0, 1, 0.1);
            }
            this.animations.toggleColumn = { animation, column };
        }
    }
}
